import { useEffect, useMemo, useState } from 'react';
import { ServiceProcedure, ServiceProcedureConsumable } from '../../types/companyResourceTypes';
import { PMPartsAndProcedure } from '../../types/pmTypes';
import {
    applyCategorySortOrder,
    filterServiceProcedures,
    findExistingCategoryName,
    groupProceduresByCategory,
    linkedConsumablesForProcedure,
    parseProcedureConsumableQuantity,
    procedureConsumableRateLabel,
} from '../../utils/companyResourceUtils';
import {
    listServiceProceduresByCompanyId,
    listServiceProcedureConsumablesByCompanyId,
    listServiceItemsByCompanyId,
    deleteServiceProcedure,
    updateServiceProcedure,
    createServiceProcedureConsumable,
    updateServiceProcedureConsumable,
    deleteServiceProcedureConsumable,
} from '../../graphql/companyResourceQueries';
import {
    AccordionCard,
    Btn,
    BtnIconOnly,
    DataTable,
    IconAlertCircle,
    IconCheckCircle,
    IconClose,
    IconCopy,
    IconEdit,
    IconPlus,
    IconTrash,
    InputField,
} from '../ui';
import type { AccordionCardItem, DataTableColumn } from '../ui';
import { ServiceProcedureModal } from './ServiceProcedureModal';
import { DeleteServiceProcedureModal } from './DeleteServiceProcedureModal';
import {
    DeleteServiceProcedureConsumableModal,
    type UnlinkConsumableTarget,
} from './DeleteServiceProcedureConsumableModal';
import { ServiceProcedureConsumablePicker } from './ServiceProcedureConsumablePicker';
import { LOCAL_STORAGE_KEYS } from '../../utils/constants';

interface ServiceProceduresTabProps {
    isAdmin: boolean;
    companyId: string;
}

type LinkedConsumableRow = {
    linkId: string;
    itemName: string;
    category: string;
    quantity: number | null;
    unitOfMeasure: string;
    missing: boolean;
};

const ProcedureConsumableQuantityField = ({
    linkId,
    itemName,
    procedureName,
    quantity,
    unitOfMeasure,
    onCommit,
}: {
    linkId: string;
    itemName: string;
    procedureName: string;
    quantity: number | null;
    unitOfMeasure: string;
    onCommit: (linkId: string, raw: string) => void;
}) => {
    const [value, setValue] = useState(quantity != null ? String(quantity) : '');
    const umId = unitOfMeasure ? `procedure-consumable-um-${linkId}` : undefined;

    useEffect(() => {
        setValue(quantity != null ? String(quantity) : '');
    }, [linkId, quantity]);

    return (
        <InputField
            className="cr-consumable-qty-field"
            inputId={`procedure-consumable-qty-${linkId}`}
            type="number"
            inputMode="decimal"
            step="any"
            min={0}
            value={value}
            onChange={setValue}
            onBlur={() => onCommit(linkId, value)}
            ariaLabel={`Quantity of ${itemName} for ${procedureName}`}
            suffix={unitOfMeasure || undefined}
            suffixId={umId}
            ariaDescribedBy={umId}
        />
    );
};

const readStoredIds = (key: string): string[] => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed)
            ? parsed.filter((id): id is string => typeof id === 'string' && id.length > 0)
            : [];
    } catch {
        return [];
    }
};

const writeStoredIds = (key: string, ids: string[]) => {
    try {
        if (ids.length === 0) {
            localStorage.removeItem(key);
            return;
        }
        localStorage.setItem(key, JSON.stringify(ids));
    } catch {
        // Ignore quota / private-mode failures; in-memory state still works for the session.
    }
};

export const ServiceProceduresTab = ({ isAdmin, companyId }: ServiceProceduresTabProps) => {
    const categoriesKey = `${LOCAL_STORAGE_KEYS.SERVICE_PROCEDURES_EXPANDED_CATEGORIES}:${companyId}`;
    const proceduresKey = `${LOCAL_STORAGE_KEYS.SERVICE_PROCEDURES_EXPANDED_ITEMS}:${companyId}`;

    const [items, setItems] = useState<ServiceProcedure[]>([]);
    const [links, setLinks] = useState<ServiceProcedureConsumable[]>([]);
    const [catalog, setCatalog] = useState<PMPartsAndProcedure[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState<ServiceProcedure | null>(null);
    const [copyingItem, setCopyingItem] = useState<ServiceProcedure | null>(null);
    const [addToCategory, setAddToCategory] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [renamingCategory, setRenamingCategory] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const [renameError, setRenameError] = useState('');
    const [renaming, setRenaming] = useState(false);
    const [deletingItem, setDeletingItem] = useState<ServiceProcedure | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [pickerProcedure, setPickerProcedure] = useState<ServiceProcedure | null>(null);
    const [savingItemId, setSavingItemId] = useState<string | null>(null);
    const [removingLinkId, setRemovingLinkId] = useState<string | null>(null);
    const [unlinkingTarget, setUnlinkingTarget] = useState<UnlinkConsumableTarget | null>(null);
    const [expandedIds, setExpandedIds] = useState<string[]>(() =>
        companyId ? readStoredIds(categoriesKey) : [],
    );
    const [expandedProcedureIds, setExpandedProcedureIds] = useState<string[]>(() =>
        companyId ? readStoredIds(proceduresKey) : [],
    );

    useEffect(() => {
        setExpandedIds(companyId ? readStoredIds(categoriesKey) : []);
        setExpandedProcedureIds(companyId ? readStoredIds(proceduresKey) : []);
    }, [companyId, categoriesKey, proceduresKey]);

    useEffect(() => {
        if (!companyId) return;
        let isCancelled = false;
        (async () => {
            try {
                setIsLoading(true);
                setError('');
                const itemsData = await listServiceProceduresByCompanyId(companyId);
                if (isCancelled) return;
                setItems(itemsData);

                const [linksResult, catalogResult] = await Promise.allSettled([
                    listServiceProcedureConsumablesByCompanyId(companyId),
                    listServiceItemsByCompanyId(companyId),
                ]);
                if (isCancelled) return;

                if (linksResult.status === 'fulfilled') {
                    setLinks(linksResult.value);
                } else {
                    console.error('Error fetching procedure consumables:', linksResult.reason);
                    setLinks([]);
                }

                if (catalogResult.status === 'fulfilled') {
                    setCatalog(catalogResult.value as PMPartsAndProcedure[]);
                } else {
                    console.error('Error fetching service item catalog:', catalogResult.reason);
                    setCatalog([]);
                }
            } catch (err) {
                console.error('Error fetching service procedures:', err);
                if (isCancelled) return;
                setError('Failed to load.');
            } finally {
                if (!isCancelled) setIsLoading(false);
            }
        })();
        return () => {
            isCancelled = true;
        };
    }, [companyId]);

    const refresh = async () => {
        const itemsData = await listServiceProceduresByCompanyId(companyId);
        setItems(itemsData);
        const [linksResult, catalogResult] = await Promise.allSettled([
            listServiceProcedureConsumablesByCompanyId(companyId),
            listServiceItemsByCompanyId(companyId),
        ]);
        setLinks(linksResult.status === 'fulfilled' ? linksResult.value : []);
        setCatalog(
            catalogResult.status === 'fulfilled'
                ? (catalogResult.value as PMPartsAndProcedure[])
                : [],
        );
    };

    const filtered = useMemo(
        () => filterServiceProcedures(items, searchQuery, categoryFilter),
        [items, searchQuery, categoryFilter],
    );
    const categories = useMemo(
        () => groupProceduresByCategory(items).map(([name]) => name),
        [items],
    );
    const sortedCategories = useMemo(() => groupProceduresByCategory(filtered), [filtered]);
    const canReorderCategories =
        isAdmin &&
        !searchQuery.trim() &&
        !categoryFilter &&
        !renamingCategory &&
        sortedCategories.length > 1;

    const handleConfirmDelete = async () => {
        if (!deletingItem) return;
        setIsDeleting(true);
        try {
            const related = links.filter((link) => link.procedureId === deletingItem.id);
            await Promise.all(related.map((link) => deleteServiceProcedureConsumable(link.id, companyId)));
            await deleteServiceProcedure(deletingItem.id, companyId);
            setItems((prev) => prev.filter((i) => i.id !== deletingItem.id));
            setLinks((prev) => prev.filter((link) => link.procedureId !== deletingItem.id));
            setDeletingItem(null);
        } catch (err) {
            console.error(err);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleSave = async () => {
        setShowModal(false);
        setEditingItem(null);
        setCopyingItem(null);
        await refresh();
    };

    const handleCategoryReorder = async (orderedIds: string[]) => {
        if (!canReorderCategories) return;
        const previous = items;
        const nextItems = applyCategorySortOrder(items, orderedIds);
        const previousOrder = new Map(previous.map((item) => [item.id, item.categorySortOrder ?? null]));
        const toUpdate = nextItems.filter((item) => (item.categorySortOrder ?? null) !== previousOrder.get(item.id));
        if (toUpdate.length === 0) return;

        setItems(nextItems);
        try {
            await Promise.all(
                toUpdate.map((item) =>
                    updateServiceProcedure({
                        id: item.id,
                        companyId,
                        categorySortOrder: item.categorySortOrder,
                    }),
                ),
            );
        } catch (err) {
            console.error('Error saving category order:', err);
            setError('Failed to save category order.');
            setItems(previous);
        }
    };

    const handleCategoryRename = async (oldName: string) => {
        const newName = renameValue.trim();
        if (!newName || newName === oldName) {
            setRenamingCategory(null);
            setRenameError('');
            return;
        }
        const conflict = findExistingCategoryName(categories, newName, oldName);
        if (conflict) {
            setRenameError(`A category named “${conflict}” already exists. Category names must be unique.`);
            return;
        }
        setRenaming(true);
        setRenameError('');
        try {
            const itemsToUpdate = items.filter((i) => i.category === oldName);
            await Promise.all(
                itemsToUpdate.map((item) => updateServiceProcedure({ id: item.id, category: newName, companyId })),
            );
            setItems((prev) => prev.map((i) => (i.category === oldName ? { ...i, category: newName } : i)));
            setExpandedIds((prev) => {
                const next = prev.map((id) => (id === oldName ? newName : id));
                writeStoredIds(categoriesKey, next);
                return next;
            });
            setRenamingCategory(null);
        } catch (err) {
            console.error('Error renaming category:', err);
            setError('Failed to rename category.');
        } finally {
            setRenaming(false);
        }
    };

    const expandProcedure = (procedureId: string) => {
        setExpandedProcedureIds((prev) => {
            if (prev.includes(procedureId)) return prev;
            const next = [...prev, procedureId];
            writeStoredIds(proceduresKey, next);
            return next;
        });
    };

    const handleAddConsumable = async (item: PMPartsAndProcedure, quantity: number) => {
        if (!pickerProcedure) return;
        setSavingItemId(item.id);
        try {
            const sortOrder = links.filter((link) => link.procedureId === pickerProcedure.id).length;
            const created = await createServiceProcedureConsumable({
                companyId,
                procedureId: pickerProcedure.id,
                serviceItemId: item.id,
                quantity,
                sortOrder,
            });
            setLinks((prev) => [...prev, created]);
            expandProcedure(pickerProcedure.id);
        } catch (err) {
            console.error('Error linking consumable:', err);
            setError('Failed to link consumable.');
        } finally {
            setSavingItemId(null);
        }
    };

    const handleQuantityBlur = async (linkId: string, raw: string) => {
        const link = links.find((entry) => entry.id === linkId);
        if (!link) return;
        const parsed = parseProcedureConsumableQuantity(raw);
        if (parsed == null) {
            setError('Enter a quantity greater than zero.');
            return;
        }
        if (parsed === link.quantity) return;
        try {
            const updated = await updateServiceProcedureConsumable({
                id: linkId,
                companyId,
                quantity: parsed,
            });
            setLinks((prev) => prev.map((entry) => (entry.id === linkId ? updated : entry)));
            setError('');
        } catch (err) {
            console.error('Error updating quantity:', err);
            setError('Failed to update quantity.');
        }
    };

    const handleConfirmRemoveConsumable = async () => {
        if (!unlinkingTarget) return;
        setRemovingLinkId(unlinkingTarget.linkId);
        try {
            await deleteServiceProcedureConsumable(unlinkingTarget.linkId, companyId);
            setLinks((prev) => prev.filter((link) => link.id !== unlinkingTarget.linkId));
            setUnlinkingTarget(null);
        } catch (err) {
            console.error('Error unlinking consumable:', err);
            setError('Failed to remove consumable.');
        } finally {
            setRemovingLinkId(null);
        }
    };

    const renderActionButtons = (item: ServiceProcedure) => (
        <div className="table-actions">
            <BtnIconOnly
                size="sm"
                icon={<IconEdit />}
                ariaLabel={`Edit ${item.itemName || 'procedure'}`}
                onClick={() => {
                    setCopyingItem(null);
                    setEditingItem(item);
                    setShowModal(true);
                }}
            />
            <BtnIconOnly
                size="sm"
                icon={<IconCopy />}
                ariaLabel={`Copy ${item.itemName || 'procedure'}`}
                onClick={() => {
                    setEditingItem(null);
                    setCopyingItem(item);
                    setShowModal(true);
                }}
            />
            <BtnIconOnly
                size="sm"
                variant="danger"
                icon={<IconTrash />}
                ariaLabel={`Delete ${item.itemName || 'procedure'}`}
                onClick={() => setDeletingItem(item)}
            />
        </div>
    );

    const renderConsumables = (procedure: ServiceProcedure) => {
        const linked = linkedConsumablesForProcedure(links, catalog, procedure.id);
        const rows: LinkedConsumableRow[] = linked.map(({ link, item }) => ({
            linkId: link.id,
            itemName: item?.itemName?.trim() || 'Unavailable item',
            category: item?.category?.trim() || '—',
            quantity: link.quantity ?? null,
            unitOfMeasure: item?.unitOfMeasure?.trim() || '',
            missing: !item,
        }));

        const columns: DataTableColumn<LinkedConsumableRow>[] = [
            {
                id: 'item',
                header: 'Service Item',
                mobileCardPrimary: true,
                renderCell: (row) => row.itemName,
            },
            {
                id: 'quantity',
                header: 'Quantity',
                renderCell: (row) =>
                    isAdmin ? (
                        <ProcedureConsumableQuantityField
                            linkId={row.linkId}
                            itemName={row.itemName}
                            procedureName={procedure.itemName}
                            quantity={row.quantity}
                            unitOfMeasure={row.unitOfMeasure}
                            onCommit={handleQuantityBlur}
                        />
                    ) : (
                        procedureConsumableRateLabel(row.quantity, row.unitOfMeasure)
                    ),
            },
            {
                id: 'category',
                header: 'Category',
                renderCell: (row) => row.category,
            },
            ...(isAdmin
                ? ([
                      {
                          id: 'actions',
                          header: 'Actions',
                          headerLabel: 'Actions',
                          align: 'center',
                          thClassName: 'data-table-col-narrow',
                          tdClassName: 'data-table-col-narrow',
                          mobileCardAction: true,
                          mobileHideLabel: true,
                          renderCell: (row) => (
                              <BtnIconOnly
                                  size="sm"
                                  variant="danger"
                                  icon={<IconTrash />}
                                  ariaLabel={`Remove ${row.itemName} from ${procedure.itemName}`}
                                  disabled={removingLinkId === row.linkId}
                                  onClick={() =>
                                      setUnlinkingTarget({
                                          linkId: row.linkId,
                                          itemName: row.itemName,
                                          procedureName: procedure.itemName,
                                      })
                                  }
                              />
                          ),
                      },
                  ] satisfies DataTableColumn<LinkedConsumableRow>[])
                : []),
        ];

        return (
            <div className="cr-procedure-consumables">
                {rows.length === 0 ? (
                    <p className="cr-procedure-consumables-empty">
                        No consumables linked yet. Add a Service Item such as a grease cartridge.
                    </p>
                ) : (
                    <DataTable
                        className="cr-category-data-table"
                        ariaLabel={`Consumables for ${procedure.itemName}`}
                        columns={columns}
                        rows={rows}
                        getRowKey={(row) => row.linkId}
                    />
                )}
                {isAdmin && (
                    <Btn
                        variant="secondary-outlined"
                        buttonSize="sm"
                        buttonIcon={<IconPlus />}
                        buttonText="Add consumable"
                        buttonOnClick={() => setPickerProcedure(procedure)}
                    />
                )}
            </div>
        );
    };

    const renderProcedureList = (catItems: ServiceProcedure[]) => {
        const procedureAccordionItems: AccordionCardItem[] = catItems.map((procedure) => {
            const linkedCount = links.filter((link) => link.procedureId === procedure.id).length;
            const countLabel = `${linkedCount} ${linkedCount === 1 ? 'consumable' : 'consumables'}`;
            const created = procedure.createdAt
                ? new Date(procedure.createdAt).toLocaleDateString()
                : '';
            return {
                id: procedure.id,
                className: 'cr-procedure-accordion-section',
                title: procedure.itemName,
                meta: created ? `${countLabel} · ${created}` : countLabel,
                actions: isAdmin ? renderActionButtons(procedure) : undefined,
                children: renderConsumables(procedure),
            };
        });

        return (
            <AccordionCard
                className="cr-procedure-cards"
                variant="light"
                corners="square"
                expandMode="multiple"
                expandedIds={expandedProcedureIds}
                onExpandedIdsChange={(ids) => {
                    const visibleIds = new Set(catItems.map((item) => item.id));
                    const hiddenStillExpanded = expandedProcedureIds.filter((id) => !visibleIds.has(id));
                    const next = [...hiddenStillExpanded, ...ids];
                    setExpandedProcedureIds(next);
                    writeStoredIds(proceduresKey, next);
                }}
                items={procedureAccordionItems}
            />
        );
    };

    const accordionItems: AccordionCardItem[] = sortedCategories.map(([cat, catItems]) => {
        const visibleItems = catItems.filter((item) => item.itemName?.trim());
        const isRenaming = renamingCategory === cat;
        const countLabel = `${visibleItems.length} ${visibleItems.length === 1 ? 'item' : 'items'}`;

        return {
            id: cat,
            className: 'cr-category-accordion-section',
            title: isRenaming ? (
                <span className="cr-category-rename-row">
                    <input
                        className={`cr-category-rename-input${renameError ? ' cr-field-error' : ''}`}
                        value={renameValue}
                        onChange={(e) => {
                            setRenameValue(e.target.value);
                            if (renameError) setRenameError('');
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleCategoryRename(cat);
                            if (e.key === 'Escape') {
                                setRenamingCategory(null);
                                setRenameError('');
                            }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                        disabled={renaming}
                        aria-label="Rename category"
                        aria-invalid={Boolean(renameError)}
                        aria-describedby={renameError ? `service-procedures-rename-error-${cat}` : undefined}
                    />
                    <Btn
                        variant="ghost"
                        buttonSize="sm"
                        buttonClasses="cr-category-rename-action"
                        buttonIcon={<IconCheckCircle />}
                        ariaLabel="Save category name"
                        buttonDisabled={renaming}
                        buttonOnClick={(e) => {
                            e.stopPropagation();
                            handleCategoryRename(cat);
                        }}
                    />
                    <Btn
                        variant="ghost"
                        buttonSize="sm"
                        buttonClasses="cr-category-rename-action"
                        buttonIcon={<IconClose />}
                        ariaLabel="Cancel rename"
                        buttonDisabled={renaming}
                        buttonOnClick={(e) => {
                            e.stopPropagation();
                            setRenamingCategory(null);
                            setRenameError('');
                        }}
                    />
                    {renameError && (
                        <span
                            id={`service-procedures-rename-error-${cat}`}
                            className="cr-field-error-text cr-category-rename-error"
                            role="alert"
                        >
                            {renameError}
                        </span>
                    )}
                </span>
            ) : (
                cat
            ),
            meta: countLabel,
            actions:
                isAdmin && cat !== 'Uncategorized' && !isRenaming ? (
                    <BtnIconOnly
                        size="sm"
                        fill="opaque"
                        icon={<IconEdit />}
                        ariaLabel={`Rename category ${cat}`}
                        onClick={() => {
                            setRenamingCategory(cat);
                            setRenameValue(cat);
                            setRenameError('');
                        }}
                    />
                ) : undefined,
            children: (
                <div className="cr-category-body">
                    {renderProcedureList(visibleItems)}
                    {isAdmin ? (
                        <div className="cr-category-add">
                            <Btn
                                variant="secondary-outlined"
                                buttonSize="sm"
                                buttonIcon={<IconPlus />}
                                buttonText="Add Procedure"
                                buttonOnClick={() => {
                                    setEditingItem(null);
                                    setCopyingItem(null);
                                    setAddToCategory(cat);
                                    setShowModal(true);
                                }}
                            />
                        </div>
                    ) : null}
                </div>
            ),
        };
    });

    const handleExpandedIdsChange = (ids: string[]) => {
        const visibleIds = new Set(sortedCategories.map(([cat]) => cat));
        const hiddenStillExpanded = expandedIds.filter((id) => !visibleIds.has(id));
        const next = [...hiddenStillExpanded, ...ids];
        setExpandedIds(next);
        if (companyId) writeStoredIds(categoriesKey, next);
    };

    if (isLoading) {
        return (
            <div className="cr-empty-state">
                <p className="cr-empty-state-text">Loading...</p>
            </div>
        );
    }

    const pickerLinkedIds = new Set(
        pickerProcedure
            ? links.filter((link) => link.procedureId === pickerProcedure.id).map((link) => link.serviceItemId)
            : [],
    );

    return (
        <div>
            <div className="cr-info-banner">
                <span className="cr-info-banner-icon" aria-hidden="true">
                    <IconAlertCircle />
                </span>
                Expand a procedure to link consumables from Service Items and assign a flat-rate quantity.
                The unit of measure comes from the Service Item. Procedures can also be saved with no
                consumable.
            </div>
            <div className="cr-toolbar">
                <input
                    className="cr-search-input"
                    type="text"
                    placeholder="Search by procedure or category..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Search procedures"
                />
                <select
                    className="cr-filter-select"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    aria-label="Filter by category"
                >
                    <option value="">All Categories</option>
                    {categories.map((c) => (
                        <option key={c} value={c}>
                            {c}
                        </option>
                    ))}
                </select>
                {isAdmin && (
                    <Btn
                        variant="primary"
                        buttonSize="sm"
                        buttonIcon={<IconPlus />}
                        buttonText="Add Procedure"
                        buttonOnClick={() => {
                            setEditingItem(null);
                            setCopyingItem(null);
                            setAddToCategory('');
                            setShowModal(true);
                        }}
                    />
                )}
            </div>
            {error && <div className="cr-error-banner">{error}</div>}

            {filtered.length === 0 ? (
                <div className="cr-empty-state">
                    <p className="cr-empty-state-text">
                        {items.length === 0
                            ? 'No procedures yet. Add your first procedure to get started.'
                            : 'No procedures match your filter.'}
                    </p>
                </div>
            ) : (
                <AccordionCard
                    className="cr-category-cards"
                    expandMode="multiple"
                    expandedIds={expandedIds}
                    onExpandedIdsChange={handleExpandedIdsChange}
                    items={accordionItems}
                    draggable={canReorderCategories}
                    onReorder={handleCategoryReorder}
                />
            )}

            {showModal && (
                <ServiceProcedureModal
                    isOpen={showModal}
                    onClose={() => {
                        setShowModal(false);
                        setEditingItem(null);
                        setCopyingItem(null);
                        setAddToCategory('');
                    }}
                    onSave={handleSave}
                    editItem={copyingItem ?? editingItem}
                    isCopy={Boolean(copyingItem)}
                    linkedConsumableCount={
                        copyingItem
                            ? links.filter((link) => link.procedureId === copyingItem.id).length
                            : 0
                    }
                    categories={categories}
                    allItems={items}
                    companyId={companyId}
                    defaultCategory={addToCategory}
                />
            )}

            {pickerProcedure && (
                <ServiceProcedureConsumablePicker
                    procedure={pickerProcedure}
                    catalog={catalog}
                    linkedServiceItemIds={pickerLinkedIds}
                    savingItemId={savingItemId}
                    onClose={() => setPickerProcedure(null)}
                    onSelect={handleAddConsumable}
                />
            )}

            <DeleteServiceProcedureModal
                item={deletingItem}
                linkedCount={
                    deletingItem
                        ? links.filter((link) => link.procedureId === deletingItem.id).length
                        : 0
                }
                isDeleting={isDeleting}
                onDismiss={() => {
                    if (!isDeleting) setDeletingItem(null);
                }}
                onConfirmDelete={handleConfirmDelete}
            />

            <DeleteServiceProcedureConsumableModal
                target={unlinkingTarget}
                isRemoving={Boolean(unlinkingTarget && removingLinkId === unlinkingTarget.linkId)}
                onDismiss={() => {
                    if (!removingLinkId) setUnlinkingTarget(null);
                }}
                onConfirmRemove={handleConfirmRemoveConsumable}
            />
        </div>
    );
};
